import * as React from 'react';
import styled from 'styled-components';

import { publish, SubscriptionTopic } from '../../../../../../../../js/messaging/pubsub';
import { Poi } from '../../../../../../../../js/viewer/elements/poi';
import { useForwardPointerEvents } from '../../../../../../../hooks/use-forward-pointer-event';
import { useSubscribe } from '../../../../../../../hooks/use-subscribe';
import { Icon } from '../../../../../../icon/icon';

type Props = {
  poi: Poi;
};

/**
 * POI Label with bottom arrow (chat-bubble style) pointing to the POI.
 */
export const PoiLabel: React.FC<Props> = (props) => {
  const imageUrl = props.poi.getThumbnailResourceUrl('64x_');
  const [selected, setSelected] = React.useState<boolean>();

  const forwardRef = useForwardPointerEvents<HTMLDivElement>(
    ['mouseup', 'pointerup', 'click'], // ignore
  );

  useSubscribe(SubscriptionTopic.NewSelection, (message) => {
    setSelected(message.uuid === props.poi.uuid);
  });

  useSubscribe(SubscriptionTopic.UnSelection, () => {
    setSelected(false);
  });

  const onSelection = () => {
    publish(SubscriptionTopic.NewSelection, props.poi);
    publish(SubscriptionTopic.AssetSelectedInViewer, props.poi);
  };

  const resolveColor = React.useCallback(() => {
    return selected ? '#ffffff' : '#ffdd33';
  }, [selected]);

  return (
    <div ref={forwardRef}>
      {imageUrl ? (
        <Bubble $color={resolveColor()} onClick={() => onSelection()}>
          <Thumbnail $color={resolveColor()} alt="POI thumbnail" src={imageUrl} />
        </Bubble>
      ) : (
        <IconBubble>
          <PoiIcon
            color={resolveColor()}
            icon={['fas', 'location-dot']}
            size="lg"
            onClick={() => onSelection()}
          />
        </IconBubble>
      )}
    </div>
  );
};

const Bubble = styled.div<{ $color: string }>`
  position: relative;
  width: 3em;
  height: 3em;
  transform: translate(0, -50%);

  pointer-events: auto;

  /* draw the arrow */
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5em; /* arrow height */
    left: 50%;
    transform: translateX(-50%);
    border-width: 0.5em 0.5em 0 0.5em;
    border-style: solid;
    border-color: ${(props) => props.$color} transparent transparent transparent;
  }

  /* make room for the arrow */
  margin-bottom: 0.5em;
  cursor: pointer;
`;

const Thumbnail = styled.img<{ $color: string }>`
  width: 100%;
  height: 100%;
  object-fit: cover;

  /* clip corners only on image */
  border: 2px solid ${(props) => props.$color};
  border-radius: 10%;
  display: block;
`;

const IconBubble = styled.div`
  transform: translate(0, -50%);
`;

const PoiIcon = styled(Icon)`
  pointer-events: auto;
  cursor: pointer;
`;
